import { useEffect, useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { fmtMoney } from "@/lib/pricing";
import { CustomerCard } from "@/components/CustomerCard";
import api from "@/lib/api";

const inputCls =
  "focus-visible:ring-2 focus-visible:ring-[#F97316]/30 focus-visible:border-[#F97316]";

export const emptyCustomer = () => ({
  id: Math.random().toString(36).slice(2),
  name: "",
  email: "",
  phone: "",
  address: "",
});

// Customer reference details (one per drop) + mower cost with a live "total with shipping" box.
export const CustomerSection = ({ customers, setCustomers, maxCustomers, mowerCost, setMowerCost, shippingPrice }) => {
  const mower = Number(mowerCost) || 0;
  const shipping = Number(shippingPrice) || 0;
  const [agents, setAgents] = useState([]);

  useEffect(() => {
    api.get("/sales-agents").then(({ data }) => setAgents(data)).catch(() => {});
  }, []);

  const upd = (idx, k, v) =>
    setCustomers(customers.map((c, i) => (i === idx ? { ...c, [k]: v } : c)));
  const addCustomer = () => setCustomers([...customers, emptyCustomer()]);
  const removeCustomer = (idx) => setCustomers(customers.filter((_, i) => i !== idx));

  return (
    <div className="pt-2 border-t border-border">
      <Label className="text-xs font-bold uppercase tracking-[0.1em] text-[#57534E]">
        Customer{customers.length > 1 ? "s" : ""}
      </Label>
      <p className="text-xs text-[#57534E] mt-0.5">
        For reference — saved with the quote (entered by the sales agent). One customer per delivery drop.
      </p>

      {customers.map((customer, idx) => (
        <CustomerCard
          key={customer.id || `cust-${idx}`}
          customer={customer}
          idx={idx}
          agents={agents}
          upd={(k, v) => upd(idx, k, v)}
          onRemove={idx > 0 ? () => removeCustomer(idx) : null}
        />
      ))}

      {customers.length < maxCustomers ? (
        <Button
          variant="outline"
          size="sm"
          onClick={addCustomer}
          className="mt-3 border-dashed border-[#F97316]/40 text-[#EA580C]"
          data-testid="add-customer-button"
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> Add customer {customers.length + 1} (of {maxCustomers} drops)
        </Button>
      ) : (
        customers.length > 1 && (
          <p className="mt-2 text-[11px] text-[#57534E]">
            One customer per drop — add another delivery stop to add more customers.
          </p>
        )
      )}

      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
        <div>
          <Label className="text-[10px] font-bold uppercase text-[#57534E]">Mower / Equipment Cost ($)</Label>
          <Input
            type="number"
            className={`mt-1 ${inputCls}`}
            placeholder="0.00"
            value={mowerCost}
            onChange={(e) => setMowerCost(e.target.value)}
            data-testid="mower-cost-input"
          />
        </div>
        <div
          className="rounded-md bg-[#FFF7ED] border border-[#F97316]/40 px-3 py-2.5"
          data-testid="equipment-total"
        >
          <div className="text-[10px] uppercase font-bold tracking-wider text-[#EA580C]">
            Total (mower + shipping) — customer reference
          </div>
          <div className="text-lg font-black text-[#1C1917]">
            {fmtMoney(mower + shipping)}
            <span className="ml-2 text-xs font-normal text-[#57534E]">
              ({fmtMoney(mower)} mower + {fmtMoney(shipping)} shipping)
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
